import { type MouseEvent, type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  listLedger,
  type LedgerFinding,
  type LedgerResponse,
  type LedgerRun,
} from "../api/codeReview";
import { listRuns } from "../api/bobReports";
import { listBobIssues, type PeterIssue } from "../api/peterHandoff";
import { useMe } from "../hooks/useMe";
import {
  SEVERITIES,
  categoryCounts,
  findingIdsSentToPeter,
  groupByOrigin,
  groupBySeverity,
  matchesSearch,
  prOf,
  relatedFindings,
  repoName,
  searchWithFinding,
} from "../lib/bobConsole";
import { fmtAgo, parseTs } from "../lib/codeReviewFormat";
import {
  findingFromSearch,
  lastCompletedRun,
  ledgerState,
  mergeLedgerPages,
  passRunning,
  prFromSearch,
  severityRank,
} from "../lib/ledgerFormat";
import { runByHead } from "../lib/bobReports";
import { mergeFiled } from "../lib/peterHandoff";
import { parseRoute } from "../lib/route";
import { absoluteTime } from "../lib/time";
import FindingDetail, { type PeterProps, type RunLinks } from "./bob/FindingDetail";
import FindingMobile from "./bob/FindingMobile";
import FindingStream, { type GroupBy } from "./bob/FindingStream";
import PackRail, { type RailPack, type RailToggle, type RailView } from "./bob/PackRail";
import type { RunNav } from "./bob/reportNav";
import { PHONE_QUERY, useMediaQuery } from "./bob/useMediaQuery";
import { usePackSummaries, type PackSummary } from "./bob/usePackSummaries";
import { type OnFiled, SendAllToPeter } from "./PeterHandoff";
import SeedReviewControl from "./SeedReviewControl";
import "./bob/bobTheme.css";
import "./bob/bobConsole.css";

/** Where the page header wants the ledger's facts and actions. Absent, they render at the top of the panel. */
export interface HeaderSlots {
  repo: HTMLElement | null;
  facts: HTMLElement | null;
  actions: HTMLElement | null;
}

type Props = {
  project: string;
  projectName?: string;
  header?: HeaderSlots;
  onSelectProject?: (id: string) => void;
  /** Where a run's report opens. Absent, the console links to no report. */
  runNav?: RunNav;
};

// Bob's ceiling on one runs read: a pack's whole review history in one page.
const RUNS_MAX_PAGE = 1000;

const REPORT_LABELS: Record<string, string> = {
  push: "Latest push report",
  seed: "Whole-pack review report",
};

/** Rows per ledger read: Bob's own default page size, asked for explicitly. */
const LEDGER_PAGE = 200;
/** Bob's ceiling on one read (`MAX_LIMIT`); the daemon clamps to it as well. */
const LEDGER_MAX_PAGE = 1000;

/** Short sha for display, or null when there is nothing to shorten.
 *
 *  `last_run` reaches the client field-for-field through the daemon's spread
 *  (only `cost_usd` is touched), which is the same passthrough that lets
 *  `counts` arrive null on a healthy answer. An upstream that omits a field
 *  therefore delivers it absent rather than as the declared type, so this
 *  returns null instead of calling `.slice` on nothing. */
function shortSha(sha: string | undefined | null): string | null {
  return sha ? sha.slice(0, 7) : null;
}

/**
 * Which degraded case this is, in plain language.
 *
 * All five map to `unavailable`, but they are different facts and a reader
 * fixing one of them needs to know which. `pr_reviewer_bad_body` and
 * `pr_reviewer_unreachable` are kept apart in particular: a service answering
 * with junk is reachable, and collapsing the two would send someone to check a
 * network path that is fine.
 *
 * The branch order matches `ledgerState`'s own read order (configured, then
 * note, upstream, error) so the two cannot disagree about which fact a body
 * carries. The trailing fallback is not reachable through `ledgerState`, which
 * returns `unavailable` only for the cases above; it exists because this
 * function does not itself establish that the body is degraded.
 */
function unavailableNote(r: LedgerResponse): string {
  if (!r.configured) return "Bob is not connected to this dashboard yet, so there is no ledger to read.";
  if (r.note === "no_github_repo") {
    return "This project has no GitHub repo the dashboard can read, so Bob was never asked about it.";
  }
  if (r.note != null) return `The dashboard did not ask Bob about this project (${r.note}).`;
  if (r.upstream !== undefined) return `Bob answered ${r.upstream} instead of a ledger, so we could not tell what he has found.`;
  if (r.error === "pr_reviewer_bad_body") {
    return "Bob answered, but not with a ledger this dashboard could read, so we could not tell what he has found.";
  }
  if (r.error === "pr_reviewer_unreachable") return "The dashboard could not reach Bob, so we could not tell what he has found.";
  if (r.error != null) return `The dashboard could not read Bob's ledger (${r.error}).`;
  return "Bob's ledger is unavailable right now, so we could not tell what he has found.";
}

/** The server's status filter, in the order a deep link tries them. */
const STATUS_TABS: readonly string[] = ["open", "fixed", "dismissed"];

/** The rail's views. Each reads one status; "Sent to Peter" keeps the open findings that have an issue. */
const VIEWS: ReadonlyArray<{ id: string; status: string; peterOnly: boolean; label: string; title: string }> = [
  { id: "open", status: "open", peterOnly: false, label: "Open", title: "Findings Bob is still raising" },
  { id: "fixed", status: "fixed", peterOnly: false, label: "Fixed", title: "A later pass looked and the defect was gone" },
  { id: "dismissed", status: "dismissed", peterOnly: false, label: "Not a bug", title: "Somebody decided these are not problems, so Bob stopped raising them" },
  { id: "peter", status: "open", peterOnly: true, label: "Sent to Peter", title: "Findings filed as a Peter issue. The list shows the ones still open." },
];

const SOURCES: ReadonlyArray<{ key: string; fromPr: boolean }> = [
  { key: "push", fromPr: false },
  { key: "pr", fromPr: true },
];

/** A push Bob skipped while a whole-pack review held the pass lock. Its
 *  `error` records the deferral, not a failure, so this is read first. */
function isDisplaced(run: LedgerRun | null | undefined): boolean {
  return typeof run?.displaced_by_seed === "number";
}

function commitHref(repo: string | undefined, sha: string): string | null {
  return repo ? `${encodeURI(`https://github.com/${repo}`)}/commit/${encodeURI(sha)}` : null;
}

/** What the last pass over this branch was, and when. */
function RunLine({ run, repo }: { run: LedgerRun; repo: string | undefined }) {
  const sha = shortSha(run.head_sha);
  const href = sha && typeof run.head_sha === "string" ? commitHref(repo, run.head_sha) : null;
  const code = sha === null ? null : href ? <a className="bob-mono" href={href} target="_blank" rel="noopener noreferrer">{sha}</a> : <code className="bob-mono">{sha}</code>;
  if (isDisplaced(run)) {
    return (
      <span className="bob-run" data-displaced="true">
        {code ? <>Push {code} skipped</> : "Last push skipped"} {fmtAgo(run.created_at)}, while a whole-pack review ran
      </span>
    );
  }
  const at = absoluteTime(parseTs(run.created_at));
  return (
    <span className="bob-run">
      {code ? <>Last reviewed {code}</> : "Last reviewed"}{at !== "" ? ` on ${at}` : ""}
      {/* Members get null by design (the daemon's money gate): "$0.00" would
          claim a pass that cost nothing, so only a real number prints. */}
      {typeof run.cost_usd === "number" && (
        <span className="bob-run-cost" title="what that pass cost">, ${run.cost_usd.toFixed(2)} for that pass</span>
      )}
    </span>
  );
}

/** The report of the pass the header names, labelled by what kind of pass it was. */
function ReportLink({ run, nav }: { run: LedgerRun; nav: RunNav }) {
  if (!Number.isSafeInteger(run.id) || run.id < 1) return null;
  const open = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    nav.openRun(run.id);
  };
  return (
    <a className="bob-head-report" href={nav.runHref(run.id)} data-run={run.id} onClick={open}>
      {REPORT_LABELS[run.kind] ?? "Latest review report"}
    </a>
  );
}

/** The pull request a `?pr=` link opened this page on. App routes on the
 *  pathname alone, so the query survives to be read here. */
function linkedPrFromUrl(): number | null {
  return typeof window === "undefined" ? null : prFromSearch(window.location.search);
}

function linkedFindingFromUrl(): number | null {
  return typeof window === "undefined" ? null : findingFromSearch(window.location.search);
}

/** On a project switch from the sidebar this panel's effects run before App
 *  rewrites the address, so the url still names the previous project and its
 *  query. It names this one only when the address itself changed (Back, Forward). */
function urlNamesProject(project: string): boolean {
  return typeof window !== "undefined" && parseRoute(window.location.pathname).projectId === project;
}

/** Set on the history entry a phone's finding view pushed, so closing that view goes Back to the entry before it. */
interface DetailState { bobDetail?: unknown }

function detailWasPushed(): boolean {
  return typeof window !== "undefined" && (window.history.state as DetailState | null)?.bobDetail === true;
}

/** Keep `?finding=` on the address in step with the selection, so the link round-trips.
 *  `push` makes it its own history entry, so Back closes what it opened. */
function writeFindingToUrl(project: string, id: number | null, push = false): void {
  if (typeof window === "undefined" || !urlNamesProject(project)) return;
  const { pathname, search, hash } = window.location;
  const next = searchWithFinding(search, id);
  if (next === search) return;
  try {
    if (push) window.history.pushState({ ...(window.history.state ?? {}), bobDetail: true }, "", `${pathname}${next}${hash}`);
    else window.history.replaceState(window.history.state, "", `${pathname}${next}${hash}`);
  } catch { /* an environment that forbids history changes keeps the old address */ }
}

/** Into the page header when the page gave one (nothing until its element exists), else in place. */
function Slot({ header, el, children }: { header: boolean; el: HTMLElement | null | undefined; children: ReactNode }) {
  if (!header) return <>{children}</>;
  return el ? createPortal(children, el) : null;
}

function toggle(list: readonly string[], key: string): string[] {
  return list.includes(key) ? list.filter((k) => k !== key) : [...list, key];
}

/**
 * Bob's ledger for a project's tracked branch, as a three-pane console: packs,
 * views and filters on the left, the findings in the middle, one finding's
 * detail on the right (its own view on a phone). Dismiss and reopen are
 * admin-only; any member may send a finding to Peter, which starts nothing.
 *
 * The one thing this panel exists to prevent is a page that says "nothing to
 * see" about a repo nobody has looked at. Until somebody pushes or an admin
 * runs a whole-pack review, a repo Bob was switched on for sends
 * `last_run: null` and no findings, which is byte-identical on the wire to a
 * reviewed repo with nothing wrong. `ledgerState` keeps those two apart, and
 * the copy below is part of the behaviour, which is why LedgerPanel.test.tsx
 * asserts on the sentences.
 */
export default function LedgerPanel({ project, projectName, header, onSelectProject, runNav }: Props) {
  // `me` still loading reads as a member: no write controls.
  const me = useMe();
  const isAdmin = me.data?.role === "admin";
  const [data, setData] = useState<LedgerResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linkedPr, setLinkedPr] = useState(linkedPrFromUrl);
  const [linkedFinding, setLinkedFinding] = useState(linkedFindingFromUrl);
  const [findingMissing, setFindingMissing] = useState(false);
  const findingTabs = useRef<Set<string> | null>(new Set());
  // Rows loaded when the link last asked for another page, so a page that adds
  // nothing ends the search on that tab.
  const findingPaged = useRef(-1);
  const firstProject = useRef(true);
  const [status, setStatus] = useState<string>("open");
  // The tab the rows on screen were read for; `status` runs ahead of it while a read is in flight.
  const shownStatus = useRef("open");
  // "Sent to Peter" reads the open tab and keeps the findings that have an issue.
  const [peterOnly, setPeterOnly] = useState(false);
  // Filters run over the rows already loaded, so the counts keep describing the repo.
  const [hiddenSeverities, setHiddenSeverities] = useState<string[]>([]);
  const [hiddenCategories, setHiddenCategories] = useState<string[]>([]);
  const [hiddenSources, setHiddenSources] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [groupBy, setGroupBy] = useState<GroupBy>("severity");
  const [selected, setSelected] = useState<number | null>(null);
  // Head commit to run id, read once a finding is opened. Null until then, and
  // after a failed read, which leaves the history without run links.
  const [runHeads, setRunHeads] = useState<{ project: string; byHead: Map<string, number> } | null>(null);
  const runsAsked = useRef<string | null>(null);
  // Set only when a link opens on a finding, so a click never scrolls the page.
  const [scrollTo, setScrollTo] = useState<number | null>(null);
  const phone = useMediaQuery(PHONE_QUERY);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  // Every read bumps this, and an answer lands only if no newer read started.
  const generation = useRef(0);
  const loadedRef = useRef(0);
  const refreshRef = useRef<() => Promise<void>>(async () => {});
  // A failed read leaves it as it was: the daemon answers an already-filed
  // finding as `existing` rather than filing it twice.
  const [peterIssues, setPeterIssues] = useState<PeterIssue[]>([]);
  const [seedKey, setSeedKey] = useState(0);
  const issuesGen = useRef(0);
  const loadIssues = useCallback(async () => {
    const gen = ++issuesGen.current;
    try {
      const issues = await listBobIssues(project);
      if (gen === issuesGen.current) setPeterIssues(issues);
    } catch { /* keep what is shown; see above */ }
  }, [project]);
  // An already-filed finding's real row is in the issues list, not the answer.
  const onFiled = useCallback<OnFiled>((created, existing) => {
    setPeterIssues((prev) => mergeFiled(prev, created));
    if (existing.length > 0) void loadIssues();
  }, [loadIssues]);
  useEffect(() => {
    setPeterIssues([]);
    void loadIssues();
  }, [loadIssues]);
  const onSeedDone = useCallback(() => {
    void loadIssues();
    return refreshRef.current();
  }, [loadIssues]);

  // Read from the start until `want` rows are loaded, so a re-read keeps
  // every page the reader had opened.
  const load = useCallback(async (want: number) => {
    const gen = ++generation.current;
    try {
      let merged: LedgerResponse | null = null;
      for (;;) {
        const offset = merged?.items.length ?? 0;
        const limit = Math.min(LEDGER_MAX_PAGE, Math.max(1, want - offset));
        const page = await listLedger(project, status, { limit, offset });
        const rows = Array.isArray(page.items) ? page.items : [];
        // A later page that came back degraded keeps what the earlier ones read.
        if (merged !== null && ledgerState(page) === "unavailable") break;
        const items = mergeLedgerPages(merged?.items ?? [], rows);
        const grew = items.length > offset;
        merged = { ...page, items, offset: 0 };
        if (!page.has_more || rows.length < limit || !grew || items.length >= want) break;
      }
      if (gen !== generation.current) return;
      loadedRef.current = merged.items.length;
      shownStatus.current = status;
      setData(merged);
      setError(null);
      setMoreError(null);
    } catch (e) {
      if (gen !== generation.current) return;
      setError((e as Error).message);
    }
  }, [project, status]);

  const refresh = useCallback(
    () => load(Math.max(LEDGER_PAGE, loadedRef.current)),
    [load],
  );
  refreshRef.current = refresh;

  const loadMore = useCallback(async () => {
    // A read already in flight would land on the pages this one adds.
    const gen = ++generation.current;
    const offset = loadedRef.current;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const page = await listLedger(project, status, { limit: LEDGER_PAGE, offset });
      if (gen !== generation.current) return;
      if (ledgerState(page) === "unavailable") {
        setMoreError(unavailableNote(page));
        return;
      }
      setData((prev) => {
        if (prev === null) return prev;
        const items = mergeLedgerPages(prev.items, Array.isArray(page.items) ? page.items : []);
        loadedRef.current = items.length;
        return { ...page, items, offset: 0 };
      });
    } catch (e) {
      if (gen === generation.current) setMoreError((e as Error).message);
    } finally {
      setLoadingMore(false);
    }
  }, [project, status]);

  // The panel stays mounted across a project switch, and a fresh read starts
  // from one page.
  const followLink = useCallback((fromUrl: boolean) => {
    const pr = fromUrl ? linkedPrFromUrl() : null;
    setLinkedPr(pr);
    if (pr !== null) setGroupBy("push");
    setLinkedFinding(fromUrl ? linkedFindingFromUrl() : null);
    setSelected(null);
    setScrollTo(null);
    setFindingMissing(false);
    findingTabs.current = new Set();
    findingPaged.current = -1;
  }, []);

  useEffect(() => {
    setData(null);
    setError(null);
    setSearch("");
    setHiddenCategories([]);
    followLink(firstProject.current || urlNamesProject(project));
    firstProject.current = false;
  }, [project, followLink]);

  useEffect(() => {
    const onPop = () => {
      if (urlNamesProject(project)) followLink(true);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [project, followLink]);

  useEffect(() => {
    const tried = findingTabs.current;
    if (linkedFinding === null || tried === null || data === null || ledgerState(data) === "unavailable") return;
    if (data.items.some((f) => f.id === linkedFinding)) {
      findingTabs.current = null;
      setSelected(linkedFinding);
      setScrollTo(linkedFinding);
      return;
    }
    const loaded = data.items.length;
    if (data.has_more && loaded < data.total && loaded > findingPaged.current && moreError === null) {
      findingPaged.current = loaded;
      void loadMore();
      return;
    }
    findingPaged.current = -1;
    tried.add(status);
    const next = STATUS_TABS.find((t) => !tried.has(t));
    setPeterOnly(false);
    if (next) {
      setStatus(next);
      return;
    }
    findingTabs.current = null;
    setFindingMissing(true);
    setStatus(STATUS_TABS[0]!);
    // Runs when a read lands or a page fails; `status` is the tab that read was for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, linkedFinding, moreError]);

  useEffect(() => {
    loadedRef.current = 0;
    void load(LEDGER_PAGE);
  }, [load]);


  useEffect(() => {
    if (!runNav || selected === null || runsAsked.current === project) return;
    runsAsked.current = project;
    listRuns(project, { limit: RUNS_MAX_PAGE })
      .then((r) => {
        if (runsAsked.current === project && Array.isArray(r.items)) setRunHeads({ project, byHead: runByHead(r.items) });
      })
      .catch(() => { /* the history names no run */ });
  }, [runNav, selected, project, seedKey]);

  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const linkedRef = useRef(linkedFinding);
  linkedRef.current = linkedFinding;
  const select = useCallback((id: number) => {
    // A pick ends a `?finding=` search still hopping tabs, and goes back to the
    // tab the picked card is on, so the search cannot override it.
    if (linkedRef.current !== null && findingTabs.current !== null) {
      setLinkedFinding(null);
      setStatus(shownStatus.current);
    }
    findingTabs.current = null;
    setFindingMissing(false);
    setSelected(id);
    setScrollTo(null);
    writeFindingToUrl(project, id, phone && selectedRef.current === null);
  }, [project, phone]);

  const deselect = useCallback(() => {
    // The popstate that Back fires clears the selection through followLink.
    if (detailWasPushed() && urlNamesProject(project)) {
      window.history.back();
      return;
    }
    setSelected(null);
    setScrollTo(null);
    writeFindingToUrl(project, null);
  }, [project]);

  // On a wide screen the detail scrolls on its own, and each finding opens at its top.
  const detailPane = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    if (detailPane.current) detailPane.current.scrollTop = 0;
  }, [selected]);

  // A pick inside the detail (a related finding) moves focus to the new title.
  const focusTitle = useRef(false);
  const selectFromDetail = useCallback((id: number) => {
    focusTitle.current = true;
    select(id);
  }, [select]);
  useEffect(() => {
    if (!focusTitle.current || selected === null) return;
    focusTitle.current = false;
    document.querySelector<HTMLElement>(`[data-detail="${selected}"] .bob-detail-title`)?.focus();
  }, [selected]);

  // The cards in the order last shown, read when the selected one leaves, so focus can go to its neighbour.
  const shownOrder = useRef<number[]>([]);
  useEffect(() => {
    if (selected === null || data === null) return;
    if (linkedFinding !== null && findingTabs.current !== null) return;
    if (data.items.some((f) => f.id === selected)) return;
    const order = shownOrder.current;
    const at = order.indexOf(selected);
    const stillThere = (id: number) => data.items.some((f) => f.id === id);
    const next = [...order.slice(at + 1), ...order.slice(0, Math.max(0, at)).reverse()].find(stillThere);
    deselect();
    const target = next === undefined
      ? document.querySelector<HTMLElement>('nav[aria-label="Views"] button[aria-pressed="true"]')
      : document.querySelector<HTMLElement>(`li[data-finding="${next}"] .bob-card`);
    target?.focus();
    // Runs when a read lands; the order is the one shown before it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selected]);

  const packIds = useMemo(() => {
    const ids = (me.data?.projects ?? []).map((p) => p.id);
    return ids.includes(project) ? ids : [project, ...ids];
  }, [me.data?.projects, project]);
  const packs = usePackSummaries(packIds, project);

  const sentToPeter = useMemo(
    () => findingIdsSentToPeter(peterIssues, data?.branch ?? data?.last_run?.branch),
    [peterIssues, data],
  );

  const onRefresh = () => {
    runsAsked.current = null;
    setSeedKey((k) => k + 1);
    void loadIssues();
    packs.refresh();
    void refresh();
  };

  const onView = (id: string) => {
    const v = VIEWS.find((x) => x.id === id);
    if (!v) return;
    if (selected !== null) deselect();
    setPeterOnly(v.peterOnly);
    setStatus(v.status);
  };

  const clearFilters = () => {
    setHiddenSeverities([]);
    setHiddenCategories([]);
    setHiddenSources([]);
    setSearch("");
  };

  const state = data === null ? null : ledgerState(data);
  const completed = data === null ? null : lastCompletedRun(data);
  // `branch` and `repo` are the daemon's; a body missing either renders its
  // locations as text rather than as links.
  const branch = data?.branch ?? data?.last_run?.branch;
  const repo = data?.repo;
  // The numbers describe a repo Bob has looked at. A never-reviewed body can
  // still carry a zeroed `counts`, and "0 open" beside "Bob has not reviewed
  // this pack yet" is the contradiction this panel exists to prevent.
  const reviewed = state === "clean" || state === "has-findings";
  const displaced = data !== null && isDisplaced(data.last_run);
  const running = data !== null && passRunning(data);
  // A failed pass leaves the earlier findings standing. A displaced push and a
  // pass still running also carry an `error`, and neither of them failed.
  const runError = data === null || displaced || running ? null : data.last_run?.error ?? null;
  const counts = reviewed ? data?.counts ?? null : null;
  // Same passthrough caveat as `last_run`: `open` is what upstream sent, so it can be absent.
  const openCounts = counts?.open ?? null;
  const openCount = openCounts ? Object.values(openCounts).reduce((sum, n) => sum + n, 0) : null;
  const items = data?.items ?? [];
  const shown = items.length;
  // `items` is a page, not the list: either field saying there is more earns the notice.
  const truncated = data !== null && (data.has_more || data.total > shown);
  const view = peterOnly ? "peter" : status;

  const shownItems = items.filter((f) => {
    if (peterOnly && !sentToPeter.has(f.id)) return false;
    if (hiddenSeverities.includes(typeof f.severity === "string" ? f.severity : "")) return false;
    if (typeof f.category === "string" && hiddenCategories.includes(f.category)) return false;
    if (hiddenSources.includes(prOf(f) === null ? "push" : "pr")) return false;
    return matchesSearch(f, search);
  });
  const groups = groupBy === "push" ? groupByOrigin(shownItems, linkedPr) : groupBySeverity(shownItems);
  const shownIds = groups.flatMap((g) => g.findings.map((f) => f.id));
  useEffect(() => { shownOrder.current = shownIds; });
  const linkMissed = linkedPr !== null && !items.some((f) => prOf(f) === linkedPr);
  const selectedRow: LedgerFinding | null = selected === null ? null : items.find((f) => f.id === selected) ?? null;
  const peter: PeterProps = { baseBranch: data?.branch, issues: peterIssues, onFiled };
  const runs: RunLinks | undefined = runNav && runHeads !== null && runHeads.project === project
    ? { runHref: runNav.runHref, openRun: runNav.openRun, runAt: (sha) => runHeads.byHead.get(sha) ?? null }
    : undefined;

  // The pack on screen is described by this panel's own read.
  const railPacks: RailPack[] = packIds.flatMap((id) => {
    const summary: PackSummary | undefined = id === project
      ? data === null ? undefined : {
        repo, branch, offRail: data.note === "no_github_repo",
        open: openCount ?? undefined,
        block: typeof openCounts?.BLOCK === "number" ? openCounts.BLOCK : undefined,
      }
      : packs.summaries.get(id);
    if (id !== project && summary?.offRail) return [];
    const name = id === project
      ? projectName ?? me.data?.projects?.find((p) => p.id === id)?.name ?? id
      : me.data?.projects?.find((p) => p.id === id)?.name ?? id;
    return [{ id, name, summary }];
  });

  const views: RailView[] = reviewed
    ? VIEWS.map((v) => ({
      id: v.id,
      label: v.label,
      title: v.title,
      // `fixed_last_7d` is a window, not a total, so the Fixed view shows no count.
      count: v.id === "open" ? openCount
        : v.id === "dismissed" ? (typeof counts?.dismissed === "number" ? counts.dismissed : null)
          : v.id === "peter" ? sentToPeter.size
            : null,
    }))
    : [];

  const severityKeys = [...SEVERITIES.map((s) => s.key), ...Object.keys(openCounts ?? {})]
    .filter((k, i, all) => all.indexOf(k) === i)
    .sort((a, b) => severityRank(a) - severityRank(b));
  const severityToggles: RailToggle[] = reviewed
    ? severityKeys.map((key) => ({
      key,
      label: key,
      note: SEVERITIES.find((s) => s.key === key)?.filterNote,
      count: status === "open" && typeof openCounts?.[key] === "number" ? openCounts[key]! : null,
      on: !hiddenSeverities.includes(key),
    }))
    : [];
  const categoryToggles: RailToggle[] = reviewed
    ? categoryCounts(items).map(({ category, count }) => ({
      key: category, label: category, count, on: !hiddenCategories.includes(category),
    }))
    : [];
  const sourceToggles: RailToggle[] = reviewed
    ? SOURCES.map((s) => ({
      key: s.key,
      label: s.fromPr ? "Pull request" : branch ? `Push to ${branch}` : "Direct push",
      count: items.filter((f) => (prOf(f) !== null) === s.fromPr).length,
      on: !hiddenSources.includes(s.key),
    }))
    : [];

  let main: ReactNode;
  if (error) {
    // `listLedger` resolves for all of Bob's failures (the daemon answers 200
    // with a degraded body), so a throw is the dashboard itself refusing.
    main = (
      <section className="bob-stream bob-stream-state">
        <div className="error-banner" role="alert">
          <span>Could not load Bob's ledger: {error}</span>
          <button type="button" onClick={() => void refresh()}>Retry</button>
        </div>
      </section>
    );
  } else if (data === null) {
    main = <section className="bob-stream bob-stream-state"><p className="bob-state">Loading…</p></section>;
  } else if (state === "unavailable") {
    // Calm and one line: a failure to reach Bob is a thing we could not tell,
    // not a thing that is broken.
    main = <section className="bob-stream bob-stream-state"><p className="bob-state">{unavailableNote(data)}</p></section>;
  } else if (state === "never-reviewed") {
    // Never a health claim: nobody has looked, and that is all this says.
    main = (
      <section className="bob-stream bob-stream-state">
        <p className="bob-state">Bob has not reviewed this pack yet.</p>
        <p className="bob-state">
          {data.last_run === null
            ? "Nothing has been pushed since Bob was switched on for this repo, so there is nothing he has looked at."
            : "No pass over this pack has finished yet, so there is nothing he has looked at."}
          {" "}This says nothing about the code.
        </p>
        {running && <p className="bob-state bob-note" data-note="running">A pass is running now.</p>}
      </section>
    );
  } else if (state === "clean") {
    const lastReviewed = shortSha(completed?.head_sha);
    main = (
      <section className="bob-stream bob-stream-state">
        <p className="bob-state bob-clear">
          Bob reviewed this pack and has no open findings.
          {lastReviewed ? ` Last reviewed commit ${lastReviewed}.` : ""}
          {runError ? <span className="bob-note" data-note="stale" title={runError}> The last pass did not finish, so it may not have looked at everything.</span> : null}
          {running ? <span className="bob-note" data-note="running"> A pass is running now.</span> : null}
          {displaced ? <span className="bob-note" data-note="deferred"> The last push was skipped while a whole-pack review ran; the next push covers it.</span> : null}
        </p>
      </section>
    );
  } else {
    const empty = items.length > 0 && peterOnly && !items.some((f) => sentToPeter.has(f.id)) ? (
      <p className="bob-state">None of the open findings on this page has been sent to Peter.</p>
    ) : items.length > 0 ? (
      // "Nothing matches the filter" is reversible; "the page came back empty" is not.
      <p className="bob-state">
        Nothing on this page matches that filter.{" "}
        <button type="button" className="bob-linkbtn bob-filter-clear" onClick={clearFilters}>Clear the filter</button>
      </p>
    ) : status === "dismissed" ? (
      // `counts.dismissed` is repo-wide and these rows are one page of it.
      typeof data.counts?.dismissed === "number" && data.counts.dismissed > 0 ? (
        <p className="bob-state">None of the {data.counts.dismissed} dismissed findings came back on this page.</p>
      ) : (
        <p className="bob-state">Nobody has dismissed a finding on this branch.</p>
      )
    ) : status === "fixed" ? (
      <p className="bob-state">No finding on this branch has been verified fixed yet.</p>
    ) : (
      <p className="bob-state">Bob has open findings on this pack, but none of them came back on this page.</p>
    );
    const summary = (
      <>
        {openCount !== null && status === "open" && <><span className="bob-total">{openCount} open</span>, </>}
        sorted worst first, then newest
        {truncated && (
          <span className="bob-truncated">
            {data.total > shown ? `, showing ${shown} of ${data.total}` : `, showing the first ${shown}, there are more`}
          </span>
        )}
      </>
    );
    main = (
      <>
        <FindingStream
          groups={groups}
          groupBy={groupBy}
          onGroupBy={setGroupBy}
          search={search}
          onSearch={setSearch}
          selected={selected}
          scrollTo={scrollTo}
          onSelect={select}
          repo={repo}
          linkedPr={groupBy === "push" ? linkedPr : null}
          summary={summary}
          tools={status === "open" && !peterOnly ? (
            <SendAllToPeter project={project} baseBranch={data.branch} openCount={openCount ?? 0} onFiled={onFiled} />
          ) : null}
          notices={(
            <>
              {runError && <p className="bob-note" data-note="stale" title={runError}>The last pass did not finish, so it may not have looked at everything.</p>}
              {running && <p className="bob-note" data-note="running">A pass is running now.</p>}
              {displaced && <p className="bob-note" data-note="deferred">The last push was skipped while a whole-pack review ran; the next push covers it.</p>}
              {linkMissed && (
                <p className="bob-note bob-link-miss">
                  The link that opened this page points at pull request #{linkedPr}, and nothing from it came back on this page.
                </p>
              )}
            </>
          )}
          empty={empty}
          footer={truncated ? (
            <div className="bob-load-more">
              <button type="button" className="bob-btn" disabled={loadingMore} onClick={() => void loadMore()}>
                {loadingMore ? "Loading…" : "Load more"}
              </button>
              <span className="bob-muted">
                {data.total > shown ? `${shown} of ${data.total} loaded` : `${shown} loaded, there are more`}
              </span>
              {moreError && <span className="bob-more-error" role="alert">Could not load more: {moreError}</span>}
            </div>
          ) : null}
        />
        {!phone && (
          <section ref={detailPane} className="bob-detail" aria-label="Finding detail">
            {selectedRow ? (
              <FindingDetail
                key={selectedRow.id}
                finding={selectedRow}
                repo={repo}
                branch={branch}
                project={project}
                isAdmin={isAdmin}
                onChanged={refresh}
                peter={peter}
                related={relatedFindings(selectedRow, items)}
                onSelect={selectFromDetail}
                completedRun={completed}
                runs={runs}
              />
            ) : (
              <p className="bob-detail-empty">Pick a finding to see what a player sees, how to reproduce it, the evidence and the fix.</p>
            )}
          </section>
        )}
      </>
    );
  }

  const usesHeader = header !== undefined;
  const repoLabel = repoName(repo);
  return (
    <div className="bob-ledger">
      <Slot header={usesHeader} el={header?.repo}>
        {repoLabel && <span className="bob-mono bob-head-repo">{repoLabel}</span>}
      </Slot>
      <Slot header={usesHeader} el={header?.facts}>
        {branch && (
          <span className="bob-chip bob-chip-cat bob-head-branch" title="The branch Bob tracks for this pack">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="6" cy="5" r="2" />
              <circle cx="6" cy="19" r="2" />
              <circle cx="18" cy="8" r="2" />
              <path d="M6 7v10M18 10c0 4-6 3-12 7" />
            </svg>
            {branch}
          </span>
        )}
        {reviewed && completed && <RunLine run={completed} repo={repo} />}
        {reviewed && completed && runNav && <ReportLink run={completed} nav={runNav} />}
        {reviewed && displaced && data?.last_run && <RunLine run={data.last_run} repo={repo} />}
      </Slot>
      <Slot header={usesHeader} el={header?.actions}>
        {/* Nothing here polls: this is how a reader picks up a pass that landed since. */}
        <button type="button" className="bob-btn bob-refresh" title="Read the ledger again" onClick={onRefresh}>
          Refresh
        </button>
        <SeedReviewControl project={project} isAdmin={isAdmin} onDone={onSeedDone} refreshKey={seedKey} />
      </Slot>
      {findingMissing && (
        <p className="bob-note bob-link-miss bob-finding-miss">
          The link names finding #{linkedFinding}, and it did not come back on any of the three tabs.
        </p>
      )}
      <div className="bob-panes">
        <PackRail
          packs={railPacks}
          current={project}
          onSelectProject={onSelectProject}
          views={views}
          view={view}
          onView={onView}
          severities={severityToggles}
          onSeverity={(key) => setHiddenSeverities((h) => toggle(h, key))}
          categories={categoryToggles}
          onCategory={(key) => setHiddenCategories((h) => toggle(h, key))}
          sources={sourceToggles}
          onSource={(key) => setHiddenSources((h) => toggle(h, key))}
        />
        {main}
      </div>
      {phone && selectedRow && state === "has-findings" && (
        <FindingMobile
          onBack={deselect}
          finding={selectedRow}
          repo={repo}
          branch={branch}
          project={project}
          isAdmin={isAdmin}
          onChanged={refresh}
          peter={peter}
          related={relatedFindings(selectedRow, items)}
          onSelect={selectFromDetail}
          completedRun={completed}
          runs={runs}
        />
      )}
    </div>
  );
}
